import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react'

const pad2 = v => String(v || '').padStart(2, '0')

const toISO = d =>
	`${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

const parseISO = iso => {
	if (!iso || typeof iso !== 'string') return { dd: 'dd', mm: 'mm', yy: 'yy' }
	const p = iso.split('-')
	if (p.length !== 3) return { dd: 'dd', mm: 'mm', yy: 'yy' }
	return { dd: pad2(p[2]), mm: pad2(p[1]), yy: String(p[0]).slice(-2) }
}

const monthNamesEn = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
]
const weekdaysEn = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const startOfMonth = (y, m) => new Date(y, m, 1)
const daysInMonth = (y, m) => new Date(y, m + 1, 0).getDate()
const getMondayStartIndex = jsDay => (jsDay === 0 ? 6 : jsDay - 1)

const clamp = (v, min, max) => Math.max(min, Math.min(max, v))

export const useDatePickerField = ({ valueISO, onChange, minISO }) => {
	const todayISO = useMemo(() => toISO(new Date()), [])
	const minDateISO = useMemo(() => {
		if (typeof minISO === 'string' && minISO) return minISO
		return todayISO
	}, [minISO, todayISO])

	const safeValueISO = typeof valueISO === 'string' ? valueISO : ''
	const parts = useMemo(() => parseISO(safeValueISO), [safeValueISO])

	const initialBase = useMemo(() => {
		if (safeValueISO) {
			const p = safeValueISO.split('-')
			if (p.length === 3) {
				const yyyy = Number(p[0])
				const mm = Number(p[1]) - 1
				if (Number.isFinite(yyyy) && Number.isFinite(mm)) return { yyyy, mm }
			}
		}
		const now = new Date()
		return { yyyy: now.getFullYear(), mm: now.getMonth() }
	}, [safeValueISO])

	const [viewYear, setViewYear] = useState(initialBase.yyyy)
	const [viewMonth, setViewMonth] = useState(initialBase.mm)

	const [isOpen, setIsOpen] = useState(false)

	const pickerRef = useRef(null)
	const calendarRef = useRef(null)

	const [calendarStyle, setCalendarStyle] = useState(null)

	const computePosition = useCallback(() => {
		const el = pickerRef.current
		if (!el) return

		const rect = el.getBoundingClientRect()
		const CAL_W = 280
		const CAL_H = 330
		const GAP = 10
		const PAD = 8

		const vw = window.innerWidth
		const vh = window.innerHeight

		const spaceBottom = vh - rect.bottom
		const spaceTop = rect.top
		const openDown = spaceBottom >= CAL_H + GAP || spaceBottom >= spaceTop

		let top = openDown ? rect.bottom + GAP : rect.top - GAP - CAL_H
		top = clamp(top, PAD, vh - CAL_H - PAD)

		let left = rect.right - CAL_W
		left = clamp(left, PAD, vw - CAL_W - PAD)

		setCalendarStyle({ top, left, width: CAL_W })
	}, [])

	useLayoutEffect(() => {
		if (!isOpen) return
		const raf = requestAnimationFrame(() => computePosition())
		return () => cancelAnimationFrame(raf)
	}, [isOpen, computePosition, viewMonth, viewYear])

	useEffect(() => {
		if (!isOpen) return
		const onResize = () => computePosition()
		window.addEventListener('resize', onResize)
		window.addEventListener('scroll', onResize, true)
		return () => {
			window.removeEventListener('resize', onResize)
			window.removeEventListener('scroll', onResize, true)
		}
	}, [isOpen, computePosition])

	useEffect(() => {
		if (!isOpen) return

		const onDown = e => {
			const t = e.target
			if (!(t instanceof Element)) return
			const inPicker = pickerRef.current?.contains(t)
			const inCal = calendarRef.current?.contains(t)
			if (!inPicker && !inCal) setIsOpen(false)
		}

		const onEsc = e => {
			if (e.key === 'Escape') setIsOpen(false)
		}

		window.addEventListener('mousedown', onDown)
		window.addEventListener('keydown', onEsc)
		return () => {
			window.removeEventListener('mousedown', onDown)
			window.removeEventListener('keydown', onEsc)
		}
	}, [isOpen])

	useEffect(() => {
		if (!isOpen) return
		if (!safeValueISO) return
		const p = safeValueISO.split('-')
		if (p.length !== 3) return
		const yyyy = Number(p[0])
		const mm = Number(p[1]) - 1
		if (!Number.isFinite(yyyy) || !Number.isFinite(mm)) return
		setViewYear(yyyy)
		setViewMonth(mm)
	}, [isOpen, safeValueISO])

	const open = useCallback(() => setIsOpen(true), [])
	const close = useCallback(() => setIsOpen(false), [])

	const onKeyDownPicker = useCallback(
		e => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault()
				open()
			}
		},
		[open]
	)

	const prevMonth = useCallback(() => {
		setViewMonth(prev => {
			if (prev > 0) return prev - 1
			setViewYear(y => y - 1)
			return 11
		})
	}, [])

	const nextMonth = useCallback(() => {
		setViewMonth(prev => {
			if (prev < 11) return prev + 1
			setViewYear(y => y + 1)
			return 0
		})
	}, [])

	const isDisabledDay = useCallback(
		iso => {
			if (!iso) return true
			return iso < minDateISO
		},
		[minDateISO]
	)

	const isSelectedDay = useCallback(
		iso => iso && iso === safeValueISO,
		[safeValueISO]
	)
	const isToday = useCallback(iso => iso && iso === todayISO, [todayISO])

	const selectDay = useCallback(
		iso => {
			if (!iso) return
			if (isDisabledDay(iso)) return
			onChange?.(iso)
			setIsOpen(false)
		},
		[isDisabledDay, onChange]
	)

	const calendarTitle = useMemo(
		() => `${monthNamesEn[viewMonth]} ${viewYear}`,
		[viewMonth, viewYear]
	)

	const gridDays = useMemo(() => {
		const first = startOfMonth(viewYear, viewMonth)
		const firstIdx = getMondayStartIndex(first.getDay())
		const dim = daysInMonth(viewYear, viewMonth)

		const cells = new Array(42).fill(null)
		for (let day = 1; day <= dim; day++) {
			const cellIndex = firstIdx + (day - 1)
			const d = new Date(viewYear, viewMonth, day)
			const iso = toISO(d)
			cells[cellIndex] = {
				day,
				iso,
				aria: `${monthNamesEn[viewMonth]} ${day}, ${viewYear}`,
			}
		}
		return cells
	}, [viewYear, viewMonth])

	return {
		parts,
		isOpen,
		calendarTitle,
		weekdays: weekdaysEn,
		gridDays,

		pickerRef,
		calendarRef,
		calendarStyle,

		open,
		close,
		prevMonth,
		nextMonth,
		selectDay,
		onKeyDownPicker,

		isSelectedDay,
		isToday,
		isDisabledDay,
	}
}
